import { FC, useContext, useEffect, useRef, useState } from "react";
import orderBy from "lodash/orderBy";
import { MultiselectItem, MultiselectOnChangeResponse, QESMultiSelect } from "@app/shared/qes-standards/forms/Multiselect";
import { CheckboxStateEnum, QESCheckbox } from "@app/shared/qes-standards/forms/Checkbox";
import LoadingSpinner from "@app/shared/LoadingSpinner";
import { FlyoutContext, FlyoutFooter, withFlyoutContext } from "@app/shared/Flyouts";
import UserService from "@app/user/userService";
import { QESButton } from "@app/shared/qes-standards/Button";
import { QESSelect } from "@app/shared/qes-standards/forms/Select";
import { QESTextInput } from "@app/shared/qes-standards/forms/TextInput";
import RoleModel from "@app/user/roleModel";
import { ClientListItem } from "@app/client/client";
import { ClientService } from "@app/client/clientService";
import { MinimalUser } from "@app/admin/user-administration/minimalUser";
import RoleGroupService from "@app/admin/role-groups/roleGroupService";
import { RoleGroupModel } from "@app/admin/role-groups/roleGroup";

interface CreatePanelProps {
  currentUserid: number;
  defaultClient: ClientListItem;
  handleSave: (onClose: () => void, name: string, selectedRoles: number[], isActive: boolean, selectedClientId: number, editUserIds: number[], roleGroupId?: number, assignToUserIds?: number[]) => void;
  allowClientEdit: boolean;
  loading: boolean;
  roleGroup?: RoleGroupModel;
}


const WrappedCreateEditPanelContent: FC<CreatePanelProps> = (props: CreatePanelProps) => {
  const flyoutContext = useContext(FlyoutContext);
  const roleGroupService = useRef(new RoleGroupService()).current;
  const clientService = useRef(new ClientService()).current;
  const userService = useRef(new UserService()).current;
  const nameValidationIntervalRef = useRef<number>(null);

  const [assignableUsers, setAssignableUsers] = useState<MinimalUser[]>([]);
  const [assignedUserOptions, setAssignedUserOptions] = useState<Record<string, MultiselectItem>>({});
  const [assignedUsers, setAssignedUsers] = useState(props.roleGroup?.users || []);
  const [authorizedUsers, setAuthorizedUsers] = useState([]);
  const [availableRoles, setAvailableRoles] = useState([]);
  const [clientOptions, setClientOptions] = useState([]);
  const [isActive, setIsActive] = useState(props.roleGroup?.isActive ?? true);
  const [loading, setLoading] = useState(false);
  const [loadingRoles, setLoadingRoles] = useState(false);
  const [name, setName] = useState(props.roleGroup?.name || "");
  const [nameIsValid, setNameIsValid] = useState(props.roleGroup?.name.length > 0);
  const [nameIsValidText, setNameIsValidText] = useState("");
  const [roleOptions, setRoleOptions] = useState<Record<string, MultiselectItem>>({});
  const [selectedClientId, setSelectedClientId] = useState(props.roleGroup?.clientId ?? props.defaultClient.clientId);
  const [selectedRoles, setSelectedRoles] = useState(props.roleGroup?.roles || []);
  const [selectedUsers, setSelectedUsers] = useState(props.roleGroup?.editUsers || []);
  const [userOptions, setUserOptions] = useState<Record<string, MultiselectItem>>({});

  useEffect(() => {
    setLoading(true);
    const onMount = async () => {
      let clients: ClientListItem[] = [props.defaultClient];

      if (props.allowClientEdit) {
        clients = await clientService.getActiveClientsList();
      }

      handleClientChange(selectedClientId);

      setClientOptions(clients.map(x => <option key={x.clientId} value={x.clientId}>{x.name}</option>));
      setLoading(false);
    }

    onMount();
  }, []);

  const handleClientChange = async (clientId: number) => {
    setLoadingRoles(true);

    let roles: RoleModel[] = await clientService.getClientRoles(clientId);
    roles = orderBy(roles, (x) => x.name, "asc");

    let newAuthorizedUsers: MinimalUser[] = await roleGroupService.getUsersAuthorizedForEdit(clientId);
    newAuthorizedUsers = orderBy(newAuthorizedUsers, (x) => x.userName, "asc");

    let newAssignableUsers: MinimalUser[] = await userService.getMinimalUsersByClient(clientId);
    newAssignableUsers = orderBy(newAssignableUsers, (x) => x.userName, "asc");

    const newSelectedUsers = selectedUsers;
    if (!props.roleGroup?.roleGroupId && !newSelectedUsers.some(x => x.userId === props.currentUserid)) {
      const currentUser = newAuthorizedUsers.find(x => x.userId === props.currentUserid);
      if (!!currentUser) {
        newSelectedUsers.push(currentUser);
      }
    }

    setAssignableUsers(newAssignableUsers);
    setAssignedUserOptions(buildUserOptions(newAssignableUsers, assignedUsers));
    setAssignedUsers(assignedUsers.filter(au => newAssignableUsers.some(u => u.userId === au.userId)));
    setAuthorizedUsers(newAuthorizedUsers);
    setAvailableRoles(roles);
    setLoadingRoles(false);
    setRoleOptions(buildRoleOptions(roles, selectedRoles));
    setSelectedRoles(selectedRoles.filter(sr => roles.some(r => r.roleId === sr.roleId)));
    setSelectedUsers(newSelectedUsers.filter(su => newAuthorizedUsers.some(u => u.userId === su.userId)));
    setUserOptions(buildUserOptions(newAuthorizedUsers, newSelectedUsers));
  }

  const updateAssignedUsers = (response: MultiselectOnChangeResponse) => {
    const selectedUserIds = response.checkedKeys;
    const newUsers = selectedUserIds.map((userId) => {
      const matchedUserModel = assignableUsers.find((u) => u.userId.toString() === userId);
      return matchedUserModel;
    }).filter((r) => !!r);
    setAssignedUserOptions(buildUserOptions(assignableUsers, newUsers));
    setAssignedUsers(newUsers);
  }

  const buildRoleOptions = (roles: RoleModel[], selectedRoles: RoleModel[]): { [key: string]: MultiselectItem } => {
    const roleOptions: { [key: string]: MultiselectItem } = {};
    if (roles) {
      roles.forEach((r) => Object.assign(roleOptions,
        {
          [r.name]: {
            currentState: selectedRoles.find(x => x.roleId === r.roleId) ? CheckboxStateEnum.Checked : CheckboxStateEnum.Unchecked,
            displayValue: r.name,
          }
        }));
    }
    return roleOptions;
  }

  const updateRoles = (response: MultiselectOnChangeResponse) => {
    const selectedRoles = response.checkedKeys;
    const newRoles = selectedRoles.map((role) => {
      const matchedRoleModel = availableRoles.find((r) => r.name === role);
      return matchedRoleModel;
    }).filter((r) => !!r);
    setRoleOptions(buildRoleOptions(availableRoles, newRoles));
    setSelectedRoles(newRoles);
  }

  const buildUserOptions = (users: MinimalUser[], newSelectedUsers: MinimalUser[]): { [key: string]: MultiselectItem } => {
    const userOptions: { [key: string]: MultiselectItem } = {};
    if (users) {
      users.forEach((u) => Object.assign(userOptions,
        {
          [u.userId]: {
            currentState: newSelectedUsers.find(x => x.userId === u.userId) ? CheckboxStateEnum.Checked : CheckboxStateEnum.Unchecked,
            displayValue: u.userName,
          }
        }));
    }
    return userOptions;
  }

  const updateUsers = (response: MultiselectOnChangeResponse) => {
    const selectedUsers = response.checkedKeys;
    const newUsers = selectedUsers.map((userId) => {
      const matchedUserModel = authorizedUsers.find((u) => u.userId.toString() === userId);
      return matchedUserModel;
    }).filter((r) => !!r);
    setSelectedUsers(newUsers);
    setUserOptions(buildUserOptions(authorizedUsers, newUsers));
  }

  useEffect(() => {
    return () => {
      window.clearInterval(nameValidationIntervalRef.current);
    }
  }, [])

  const validateName = async (newName: string, clientId: number) => {
    window.clearInterval(nameValidationIntervalRef.current);
    nameValidationIntervalRef.current = window.setTimeout(async () => {
      if (!newName) {
        setNameIsValid(false);
        setNameIsValidText("");
        return;
      } else if (await roleGroupService.roleGroupNameExists(newName.trim(), clientId, props.roleGroup?.roleGroupId)) {
        setNameIsValid(false);
        setNameIsValidText("This name has already been used.");
      } else {
        setNameIsValid(true);
        setNameIsValidText("");
      }
    }, 500);
  }


  return (
    <>
      {props.loading || loading ? <LoadingSpinner /> :
        <>
          <QESTextInput
            testId={"create-role-group-panel-content__name"}
            containerClassName="mb-2 col-4"
            label="Name"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              validateName(e.target.value, selectedClientId);
            }}
            isValid={nameIsValid}
            errorText={nameIsValidText}
            required={true}
            wasFocusedExternally={!!props.roleGroup}
          />
          <QESSelect
            testId="create-role-group-panel-content__client"
            containerClassName="col-4 mb-2 px-2"
            label="Client"
            value={selectedClientId}
            required={true}
            isValid={!!selectedClientId.toString()}
            disabled={!props.allowClientEdit}
            onChange={(e) => {
              setSelectedClientId(parseInt(e.target.value));
              handleClientChange(parseInt(e.target.value));
              validateName(name, parseInt(e.target.value));
            }}
          >
            {clientOptions}
          </QESSelect>
          {loadingRoles ? <LoadingSpinner /> :
            <>
              <QESMultiSelect
                containerClassName="mb-2 ps-0 col-11"
                errorText="At least one role must be selected."
                label="Roles"
                onChange={updateRoles}
                options={roleOptions}
                required={true}
                testId="create-role-group-panel-content__roles"
                isValid={selectedRoles.length > 0}
              />
              <QESMultiSelect
                containerClassName="mb-2 ps-0 col-11"
                label="Role Group Admins"
                onChange={updateUsers}
                options={userOptions}
                required={true}
                testId="create-role-group-panel-content__roles"
                isValid={selectedUsers.length > 1}
                errorText="Must have a minimum of 2 users selected"
                validationTooltipContent="Must have a minimum of 2 users selected"
              />
              <QESMultiSelect
                containerClassName="mb-2 ps-0 col-11"
                label="Assign to Users"
                onChange={updateAssignedUsers}
                options={assignedUserOptions}
                testId="create-role-group-panel-content__assign-users"
                isValid={true}
              />
            </>
          }
          <QESCheckbox
            containerClassName="mb-2"
            testId={"create-role-group-panel-content__active"}
            label="Is Active"
            checked={isActive}
            onChange={(e) => setIsActive(e.target.checked)}
          />

          <FlyoutFooter
            submitButton={<QESButton
              qesTestId="create-role-group-panel-content__confirm"
              className="mt10"
              disabled={!nameIsValid || selectedRoles.length === 0 || selectedUsers.length < 2}
              onClick={() => {
                props.handleSave(flyoutContext.handleCloseFlyout, name.trim(), selectedRoles.map(x => x.roleId), isActive, selectedClientId, selectedUsers.map(x => x.userId), props.roleGroup?.roleGroupId, assignedUsers.map(x => x.userId));
              }}
            >Save</QESButton>
            }
          />
        </>
      }
    </>
  );
}

const CreateEditPanelContent = withFlyoutContext(WrappedCreateEditPanelContent);
export default CreateEditPanelContent;
